import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { crewAPI, blockedDatesAPI } from '../utils/api';
import api from '../utils/api';
import DuplicateCrewPanel from '../components/DuplicateCrewPanel';
import { Plus, Edit, Trash2, Phone, Mail, KeyRound, CalendarOff, X, Eye, EyeOff, Send, Copy, Check, Archive, ArchiveRestore, Camera } from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/helpers';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Avatar } from '../components/Avatar';

const CrewMembers = () => {
  const [crewMembers, setCrewMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [formData, setFormData] = useState({});
  const [showBlockModal, setShowBlockModal] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockForm, setBlockForm] = useState({ start_date: '', end_date: '', note: '' });
  const [showRates, setShowRates] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [sendingCredentials, setSendingCredentials] = useState(null);
  const [revealedTempPw, setRevealedTempPw] = useState({});
  const [copiedCrew, setCopiedCrew] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCrewMembers();
  }, [showArchived]);

  const fetchCrewMembers = async () => {
    setLoading(true);
    try {
      const response = await crewAPI.getAll(showArchived ? { only_archived: true } : {});
      setCrewMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch crew members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (crewId, fileObj) => {
    if (!fileObj) return;
    const fd = new FormData();
    fd.append('file', fileObj);
    try {
      await crewAPI.uploadPhoto(crewId, fd);
      toast.success('Photo updated');
      fetchCrewMembers();
    } catch {
      toast.error('Failed to upload photo');
    }
  };

  const handleCreate = () => {
    setSelectedCrew(null);
    setShowPassword(false);
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      password: '',
      role: 'Photographer',
      half_day_rate_usd: 0,
      full_day_rate_usd: 0,
      half_day_rate_inr: 0,
      full_day_rate_inr: 0,
      travel_fees_usd: 0,
      travel_fees_inr: 0,
      gear_checklist: []
    });
    setShowModal(true);
  };

  const handleEdit = (crew) => {
    setSelectedCrew(crew);
    setShowPassword(false);
    setFormData({ ...crew, password: '' });
    setShowModal(true);
  };

  const handleDelete = async (crew) => {
    const isArchived = crew.is_active === false;
    if (isArchived) {
      // Two-step: permanent-delete for already-archived crew
      const first = window.confirm(
        `Permanently DELETE ${crew.full_name}?\n\n` +
        `This will destroy their record, login account, ALL past assignments, and ALL payment history.\n\n` +
        `This is irreversible. Continue?`
      );
      if (!first) return;
      const second = window.prompt(`Type DELETE to confirm permanent removal of ${crew.full_name}:`);
      if (second !== 'DELETE') { toast.error('Cancelled — text must be exactly "DELETE"'); return; }
      try {
        await crewAPI.delete(crew.crew_id, { hard: true });
        toast.success(`${crew.full_name} permanently deleted`);
        fetchCrewMembers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to permanently delete');
      }
      return;
    }
    // Default: soft archive
    if (!window.confirm(
      `Archive ${crew.full_name}?\n\n` +
      `They will disappear from schedules and can't be assigned to new events. Past assignments and payment records stay intact. You can restore them anytime from the Archived tab.`
    )) return;
    try {
      await crewAPI.delete(crew.crew_id);
      toast.success(`${crew.full_name} archived`);
      fetchCrewMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive');
    }
  };

  const handleRestore = async (crew) => {
    try {
      await crewAPI.restore(crew.crew_id);
      toast.success(`${crew.full_name} restored`);
      fetchCrewMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (!submitData.password) delete submitData.password;
      
      if (selectedCrew) {
        await crewAPI.update(selectedCrew.crew_id, submitData);
        toast.success('Crew member updated');
      } else {
        await crewAPI.create(submitData);
        toast.success('Crew member added');
      }
      setShowModal(false);
      fetchCrewMembers();
    } catch (error) {
      toast.error('Failed to save crew member');
    }
  };

  const openBlockModal = async (crew) => {
    setShowBlockModal(crew);
    setBlockForm({ start_date: '', end_date: '', note: '' });
    try {
      const res = await blockedDatesAPI.getAll(crew.crew_id);
      setBlockedDates(res.data);
    } catch {
      toast.error('Failed to load blocked dates');
    }
  };

  const handleAddBlock = async (e) => {
    e.preventDefault();
    if (!blockForm.start_date) return;
    try {
      await blockedDatesAPI.create({
        crew_member_id: showBlockModal.crew_id,
        start_date: blockForm.start_date,
        end_date: blockForm.end_date || blockForm.start_date,
        note: blockForm.note
      });
      toast.success('Date blocked');
      setBlockForm({ start_date: '', end_date: '', note: '' });
      const res = await blockedDatesAPI.getAll(showBlockModal.crew_id);
      setBlockedDates(res.data);
    } catch {
      toast.error('Failed to block date');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      await blockedDatesAPI.delete(blockId);
      toast.success('Block removed');
      const res = await blockedDatesAPI.getAll(showBlockModal.crew_id);
      setBlockedDates(res.data);
    } catch {
      toast.error('Failed to remove block');
    }
  };

  const handleSendCredentials = async (crew) => {
    if (!crew.has_login) {
      toast.error('This crew member does not have a login account. Please set a password first.');
      return;
    }

    const passwordToSend = prompt(
      `Enter the password to include in the email for ${crew.full_name}.\n\n` +
      `This will NOT change their password — make sure you type the correct existing one.`
    );
    if (!passwordToSend || !passwordToSend.trim()) {
      return;
    }

    setSendingCredentials(crew.crew_id);
    try {
      await api.post('/api/crew/send-credentials', {
        crew_id: crew.crew_id,
        password: passwordToSend.trim()
      });

      toast.success(`Credentials sent to ${crew.email}`);
    } catch (error) {
      toast.error('Failed to send credentials');
    } finally {
      setSendingCredentials(null);
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="crew-members-page">
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold tracking-tight mb-1 sm:mb-2">Crew Members</h1>
              <p className="text-base text-muted-foreground">Manage your photography and videography team</p>
            </div>
            <button
              onClick={handleCreate}
              className="flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-black/90 transition-all active:scale-[0.97] text-sm"
              data-testid="create-crew-button"
            >
              <Plus size={16} strokeWidth={2} />
              Add Crew
            </button>
          </div>
        </div>

        {!showArchived && <DuplicateCrewPanel onMerged={fetchCrewMembers} />}

        {/* Active / Archived toggle — iOS segmented control */}
        <div className="inline-flex gap-1 mb-4 sm:mb-6 bg-secondary rounded-full p-1">
          <button
            onClick={() => setShowArchived(false)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${!showArchived ? 'bg-card text-foreground shadow-[0_1px_4px_rgba(0,0,0,0.08)]' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="crew-tab-active"
          >
            Active
          </button>
          <button
            onClick={() => setShowArchived(true)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all inline-flex items-center gap-1.5 ${showArchived ? 'bg-card text-foreground shadow-[0_1px_4px_rgba(0,0,0,0.08)]' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="crew-tab-archived"
          >
            <Archive size={13} /> Archived
          </button>
        </div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="crew-loading">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div key={i} className="card p-6 h-48 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && crewMembers.length === 0 && (
          <div className="card p-10 text-center text-sm text-muted-foreground" data-testid="crew-empty-state">
            {showArchived ? 'No archived crew members.' : 'No active crew members. Click ADD CREW to add one.'}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {crewMembers.map((crew, idx) => (
            <motion.div
              key={crew.crew_id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Math.min(idx * 0.05, 0.5), ease: 'easeOut' }}
              className={`card p-4 sm:p-6 ${crew.is_active === false ? 'opacity-70 border-dashed' : ''}`}
              data-testid={`crew-${crew.crew_id}`}
            >
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  <label className="relative cursor-pointer group shrink-0" data-testid={`crew-photo-upload-${crew.crew_id}`}>
                    <Avatar name={crew.full_name} src={crew.photo_url} size={44} />
                    <span className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <Camera size={13} className="text-white" />
                    </span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(crew.crew_id, e.target.files[0])} />
                  </label>
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-medium flex items-center gap-2">
                      {crew.full_name}
                      {crew.is_active === false && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-muted text-muted-foreground" data-testid={`crew-archived-badge-${crew.crew_id}`}>
                          Archived
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-gold">{crew.role}</p>
                  </div>
                </div>
                <div className="flex gap-1.5 sm:gap-2">
                  {crew.is_active === false ? (
                    <button
                      onClick={() => handleRestore(crew)}
                      className="p-1.5 sm:p-2 border border-green-500 text-green-600 dark:text-green-400 hover:bg-green-500/10 transition-colors"
                      data-testid={`restore-crew-${crew.crew_id}`}
                      title="Restore this crew member"
                    >
                      <ArchiveRestore size={14} />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEdit(crew)}
                      className="p-1.5 sm:p-2 border border-border hover:border-gold transition-colors"
                      data-testid={`edit-crew-${crew.crew_id}`}
                    >
                      <Edit size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(crew)}
                    className="p-1.5 sm:p-2 border border-destructive text-destructive hover:bg-destructive/10 transition-colors"
                    data-testid={`delete-crew-${crew.crew_id}`}
                    title={crew.is_active === false ? 'Permanently delete' : 'Archive'}
                  >
                    {crew.is_active === false ? <Trash2 size={14} /> : <Archive size={14} />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5 sm:space-y-2 text-sm">
                <p className="flex items-center gap-2 text-muted-foreground truncate">
                  <Mail size={14} className="flex-shrink-0" />
                  <span className="truncate">{crew.email}</span>
                </p>
                <p className="flex items-center gap-2 text-muted-foreground">
                  <Phone size={14} className="flex-shrink-0" />
                  {crew.phone}
                </p>
                <p className="flex items-center gap-2">
                  <KeyRound size={14} className={`flex-shrink-0 ${crew.has_login ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`} />
                  <span className={crew.has_login ? 'text-green-600 dark:text-green-400 text-xs' : 'text-muted-foreground text-xs'}>
                    {crew.has_login ? 'Login enabled' : 'No login account'}
                  </span>
                </p>
              </div>

              {crew.has_login && (
                <div className="mt-3 pt-3 border-t border-border" data-testid={`temp-password-section-${crew.crew_id}`}>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
                    Last Admin-Set Password
                  </p>
                  {crew.last_admin_password ? (
                    <div className="flex items-center gap-2">
                      <code
                        className="flex-1 px-2 py-1.5 bg-muted/40 border border-border text-xs font-mono truncate"
                        data-testid={`temp-password-value-${crew.crew_id}`}
                      >
                        {revealedTempPw[crew.crew_id] ? crew.last_admin_password : '••••••••'}
                      </code>
                      <button
                        type="button"
                        onClick={() => setRevealedTempPw(prev => ({ ...prev, [crew.crew_id]: !prev[crew.crew_id] }))}
                        className="p-1.5 border border-border hover:border-gold text-muted-foreground hover:text-foreground transition-colors"
                        title={revealedTempPw[crew.crew_id] ? 'Hide password' : 'Show password'}
                        data-testid={`toggle-temp-password-${crew.crew_id}`}
                      >
                        {revealedTempPw[crew.crew_id] ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(crew.last_admin_password);
                            setCopiedCrew(crew.crew_id);
                            toast.success('Password copied');
                            setTimeout(() => setCopiedCrew(null), 1500);
                          } catch {
                            toast.error('Failed to copy');
                          }
                        }}
                        className="p-1.5 border border-border hover:border-gold text-muted-foreground hover:text-foreground transition-colors"
                        title="Copy password"
                        data-testid={`copy-temp-password-${crew.crew_id}`}
                      >
                        {copiedCrew === crew.crew_id ? <Check size={14} className="text-green-600 dark:text-green-400" /> : <Copy size={14} />}
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic" data-testid={`temp-password-self-set-${crew.crew_id}`}>
                      Crew has set their own password — not visible to admin.
                    </p>
                  )}
                </div>
              )}

              <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-border">
                <button
                  onClick={() => setShowRates(prev => ({ ...prev, [crew.crew_id]: !prev[crew.crew_id] }))}
                  className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2"
                  data-testid={`toggle-rates-${crew.crew_id}`}
                >
                  {showRates[crew.crew_id] ? <EyeOff size={14} /> : <Eye size={14} />}
                  {showRates[crew.crew_id] ? 'Hide Rates' : 'Show Rates'}
                </button>
                {showRates[crew.crew_id] && (
                  <div className="space-y-1.5 sm:space-y-2 text-sm">
                    {(crew.half_day_rate_usd > 0 || crew.full_day_rate_usd > 0) && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Half Day (USD):</span>
                          <span>{formatCurrency(crew.half_day_rate_usd, 'USD')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Full Day (USD):</span>
                          <span>{formatCurrency(crew.full_day_rate_usd, 'USD')}</span>
                        </div>
                      </>
                    )}
                    {(crew.half_day_rate_inr > 0 || crew.full_day_rate_inr > 0) && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Half Day (INR):</span>
                          <span>{formatCurrency(crew.half_day_rate_inr, 'INR')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Full Day (INR):</span>
                          <span>{formatCurrency(crew.full_day_rate_inr, 'INR')}</span>
                        </div>
                      </>
                    )}
                    {!crew.half_day_rate_usd && !crew.full_day_rate_usd && !crew.half_day_rate_inr && !crew.full_day_rate_inr && (
                      <p className="text-muted-foreground text-xs">No rates set</p>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={() => openBlockModal(crew)}
                className="mt-3 w-full flex items-center justify-center gap-2 py-2 border border-border hover:border-gold text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
                data-testid={`block-dates-${crew.crew_id}`}
              >
                <CalendarOff size={14} />
                Manage Blocked Dates
              </button>
              
              {crew.has_login && (
                <button
                  onClick={() => handleSendCredentials(crew)}
                  disabled={sendingCredentials === crew.crew_id}
                  className="mt-2 w-full flex items-center justify-center gap-2 py-2 bg-gold/10 border border-gold/30 hover:bg-gold/20 text-xs uppercase tracking-wide text-gold transition-colors disabled:opacity-50"
                  data-testid={`send-credentials-${crew.crew_id}`}
                >
                  <Send size={14} />
                  {sendingCredentials === crew.crew_id ? 'Sending...' : 'Send Login Credentials'}
                </button>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="crew-modal">
          <div className="bg-background w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
            <h2 className="text-2xl font-serif mb-6">{selectedCrew ? 'Edit Crew Member' : 'Add Crew Member'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Full Name</label>
                  <input type="text" value={formData.full_name || ''} onChange={(e) => setFormData({...formData, full_name: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" required data-testid="crew-full-name" />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Role</label>
                  <select value={formData.role || 'Photographer'} onChange={(e) => setFormData({...formData, role: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" data-testid="crew-role">
                    <option>Photographer</option>
                    <option>Videographer</option>
                    <option>Cinematographer</option>
                    <option>Photographer & Videographer</option>
                    <option>Photographer & Cinematographer</option>
                    <option>Videographer & Cinematographer</option>
                    <option>Photographer, Videographer & Cinematographer</option>
                    <option>Drone Operator</option>
                    <option>Editor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Email</label>
                  <input type="email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" required data-testid="crew-email" />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Phone</label>
                  <input type="tel" value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" required data-testid="crew-phone" />
                </div>
              </div>

              {/* Login Account Section */}
              <div className="border border-gold/30 p-4 mt-4">
                <h3 className="text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
                  <KeyRound size={14} className="text-gold" />
                  Crew Login Account
                </h3>
                <p className="text-xs text-muted-foreground mb-3">
                  {selectedCrew?.has_login 
                    ? 'This crew member has a login account. Enter a new password to reset it, or leave blank to keep current password.' 
                    : 'Set a password to enable this crew member to log in and view their assigned events.'}
                </p>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Password</label>
                  <div className="relative">
                    <input 
                      type={showPassword ? 'text' : 'password'} 
                      value={formData.password || ''} 
                      onChange={(e) => setFormData({...formData, password: e.target.value})} 
                      className="w-full px-3 py-2 pr-10 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" 
                      placeholder={selectedCrew?.has_login ? 'Leave blank to keep current' : 'Set login password'} 
                      data-testid="crew-password" 
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Rates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Half Day Rate (USD)</label>
                  <input type="number" value={formData.half_day_rate_usd || 0} onChange={(e) => setFormData({...formData, half_day_rate_usd: parseFloat(e.target.value)})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" min="0" step="0.01" data-testid="crew-half-day-usd" />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Full Day Rate (USD)</label>
                  <input type="number" value={formData.full_day_rate_usd || 0} onChange={(e) => setFormData({...formData, full_day_rate_usd: parseFloat(e.target.value)})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" min="0" step="0.01" data-testid="crew-full-day-usd" />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Half Day Rate (INR)</label>
                  <input type="number" value={formData.half_day_rate_inr || 0} onChange={(e) => setFormData({...formData, half_day_rate_inr: parseFloat(e.target.value)})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" min="0" step="0.01" data-testid="crew-half-day-inr" />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Full Day Rate (INR)</label>
                  <input type="number" value={formData.full_day_rate_inr || 0} onChange={(e) => setFormData({...formData, full_day_rate_inr: parseFloat(e.target.value)})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" min="0" step="0.01" data-testid="crew-full-day-inr" />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Travel Fees (USD)</label>
                  <input type="number" value={formData.travel_fees_usd || 0} onChange={(e) => setFormData({...formData, travel_fees_usd: parseFloat(e.target.value)})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" min="0" step="0.01" data-testid="crew-travel-usd" />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Travel Fees (INR)</label>
                  <input type="number" value={formData.travel_fees_inr || 0} onChange={(e) => setFormData({...formData, travel_fees_inr: parseFloat(e.target.value)})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" min="0" step="0.01" data-testid="crew-travel-inr" />
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button type="submit" className="bg-primary text-primary-foreground px-6 py-3 hover:bg-primary/90" data-testid="crew-submit">
                  {selectedCrew ? 'UPDATE' : 'ADD'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="border border-border px-6 py-3 hover:bg-accent" data-testid="crew-cancel">
                  CANCEL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Blocked Dates Modal */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setShowBlockModal(null)}>
          <div className="bg-background w-full sm:max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-8 rounded-t-2xl sm:rounded-none" onClick={e => e.stopPropagation()} data-testid="block-dates-modal">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-serif">Blocked Dates</h2>
                <p className="text-sm text-muted-foreground">{showBlockModal.full_name}</p>
              </div>
              <button onClick={() => setShowBlockModal(null)} className="p-2 hover:bg-accent"><X size={18} /></button>
            </div>

            {/* Add new block form */}
            <form onSubmit={handleAddBlock} className="mb-6 p-4 border border-border space-y-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Add Blocked Dates</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1">Start Date</label>
                  <input
                    type="date"
                    value={blockForm.start_date}
                    onChange={e => setBlockForm({...blockForm, start_date: e.target.value, end_date: blockForm.end_date || e.target.value})}
                    className="w-full px-3 py-2 border border-input bg-background text-sm"
                    required
                    data-testid="block-start-date"
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1">End Date</label>
                  <input
                    type="date"
                    value={blockForm.end_date}
                    onChange={e => setBlockForm({...blockForm, end_date: e.target.value})}
                    min={blockForm.start_date}
                    className="w-full px-3 py-2 border border-input bg-background text-sm"
                    data-testid="block-end-date"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1">Note (optional)</label>
                <input
                  type="text"
                  value={blockForm.note}
                  onChange={e => setBlockForm({...blockForm, note: e.target.value})}
                  placeholder="e.g. Patel Wedding, Personal day"
                  className="w-full px-3 py-2 border border-input bg-background text-sm"
                  data-testid="block-note"
                />
              </div>
              <button type="submit" className="w-full bg-primary text-primary-foreground py-2 text-sm hover:bg-black/90" data-testid="block-submit">
                BLOCK DATES
              </button>
            </form>

            {/* Existing blocks */}
            <div className="space-y-2">
              {blockedDates.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No blocked dates</p>
              ) : (
                blockedDates.map(block => (
                  <div key={block.block_id} className="flex items-center justify-between p-3 border border-border" data-testid={`block-${block.block_id}`}>
                    <div>
                      <p className="text-sm font-medium">
                        {formatDate(block.start_date)}
                        {block.start_date !== block.end_date && ` — ${formatDate(block.end_date)}`}
                      </p>
                      {block.note && <p className="text-xs text-muted-foreground">{block.note}</p>}
                    </div>
                    <button
                      onClick={() => handleDeleteBlock(block.block_id)}
                      className="p-1.5 text-destructive hover:bg-destructive/10 transition-colors"
                      data-testid={`delete-block-${block.block_id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CrewMembers;
